import React, { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { PageHeader } from '@/components/layout'
import { ktpService, type KTPAdminItem } from '@/services/ktpService'

/**
 * AdminKTPReviewPage
 * -------------------
 * M4: admin inbox for KTP verification submissions. Admins with `ktp.view`
 * can see pending rows; `ktp.approve` / `ktp.reject` gated actions.
 */
export const AdminKTPReviewPage: React.FC = () => {
    const queryClient = useQueryClient()
    const { data: resp, isLoading } = useQuery({
        queryKey: ['admin-ktp-pending'],
        queryFn: () => ktpService.listPending(20, 0),
    })
    const items: KTPAdminItem[] = resp?.data ?? []

    const [selected, setSelected] = useState<KTPAdminItem | null>(null)
    const [rejectReason, setRejectReason] = useState('')
    const [rejectError, setRejectError] = useState<string | null>(null)
    const [actionError, setActionError] = useState<string | null>(null)

    const openDetail = async (item: KTPAdminItem) => {
        setSelected(item)
        setRejectReason('')
        setRejectError(null)
        setActionError(null)
        const detail = await ktpService.getDetail(item.userId)
        if (detail.success && detail.data) {
            setSelected(detail.data)
        }
    }

    const closeDetail = () => {
        setSelected(null)
    }


    const verifyMutation = useMutation({
        mutationFn: (userId: string) => ktpService.verify(userId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-ktp-pending'] })
            closeDetail()
        },
        onError: (err: Error) => setActionError(err.message || 'Gagal menyetujui KTP'),
    })

    const rejectMutation = useMutation({
        mutationFn: ({ userId, reason }: { userId: string; reason: string }) =>
            ktpService.reject(userId, reason),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-ktp-pending'] })
            closeDetail()
        },
        onError: (err: Error) => setActionError(err.message || 'Gagal menolak KTP'),
    })

    const submitReject = () => {
        if (!selected) return
        if (rejectReason.trim().length < 20) {
            setRejectError('Alasan penolakan minimal 20 karakter')
            return
        }
        setRejectError(null)
        rejectMutation.mutate({ userId: selected.userId, reason: rejectReason })
    }

    return (
        <div data-testid="admin-ktp-review-page">
            <PageHeader
                title="Verifikasi KTP"
                subtitle="Review dan setujui/tolak pengajuan KTP peserta hybrid"
            />

            <div className="bg-white dark:bg-surface-card rounded-lg border border-gray-200 dark:border-white/5 overflow-hidden">
                <div className="overflow-x-auto">
                <table className="w-full text-left">
                    <thead className="bg-gray-100 dark:bg-white/5 text-xs uppercase text-gray-500 dark:text-gray-400">
                        <tr>
                            <th className="px-4 py-3">Nama</th>
                            <th className="px-4 py-3">Email</th>
                            <th className="px-4 py-3 hidden md:table-cell">Dikirim</th>
                            <th className="px-4 py-3 hidden md:table-cell">Ditolak</th>
                            <th className="px-4 py-3 text-right">Aksi</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-white/5">
                        {isLoading && (
                            <tr>
                                <td colSpan={5} className="p-6 text-center text-gray-500" data-testid="ktp-list-loading">
                                    Memuat...
                                </td>
                            </tr>
                        )}
                        {!isLoading && items.length === 0 && (
                            <tr>
                                <td colSpan={5} className="p-6 text-center text-gray-500" data-testid="ktp-list-empty">
                                    Tidak ada pengajuan pending.
                                </td>
                            </tr>
                        )}
                        {items.map((item) => (
                            <tr key={item.userId} data-testid={`ktp-row-${item.userId}`} className="hover:bg-gray-100 dark:hover:bg-white/5">
                                <td className="px-4 py-3">{item.name}</td>
                                <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{item.email}</td>
                                <td className="px-4 py-3 text-sm hidden md:table-cell">
                                    {item.submittedAt ? new Date(item.submittedAt).toLocaleString('id-ID') : '-'}
                                </td>
                                <td className="px-4 py-3 text-sm hidden md:table-cell">{item.rejectedCount}</td>
                                <td className="px-4 py-3 text-right">
                                    <button
                                        type="button"
                                        data-testid={`ktp-open-${item.userId}`}
                                        onClick={() => openDetail(item)}
                                        className="px-3 py-1.5 bg-primary text-white rounded-lg text-sm font-semibold"
                                    >
                                        Review
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                </div>
            </div>

            {/* Review Modal */}
            {selected && (
                <div
                    role="dialog"
                    aria-modal="true"
                    data-testid="ktp-review-modal"
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
                    onClick={closeDetail}
                >
                    <div
                        className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full p-6 shadow-xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 className="text-lg font-bold mb-4">Review KTP — {selected.name}</h3>

                        <div className="grid md:grid-cols-2 gap-4 text-sm">
                            <div className="space-y-2">
                                <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-700">
                                    <span className="text-xs text-yellow-700 dark:text-yellow-400 font-semibold uppercase">Nama Akun (cocokkan dengan foto KTP)</span>
                                    <div className="mt-1 font-bold text-base text-gray-900 dark:text-white" data-testid="ktp-modal-name">
                                        {selected.name}
                                    </div>
                                </div>
                                <div>
                                    <span className="text-gray-500">NIK:</span>{' '}
                                    <span className="font-mono font-semibold" data-testid="ktp-modal-nik">
                                        {selected.ktpNik || '-'}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-gray-500">Tgl Lahir (KTP):</span>{' '}
                                    <span data-testid="ktp-modal-birth">
                                        {selected.ktpBirthDate
                                            ? new Date(selected.ktpBirthDate).toLocaleDateString('id-ID')
                                            : '-'}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-gray-500">Tgl Lahir (Profil):</span>{' '}
                                    <span>
                                        {selected.birthDate
                                            ? new Date(selected.birthDate).toLocaleDateString('id-ID')
                                            : '-'}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-gray-500">Email:</span> {selected.email}
                                </div>
                                <div>
                                    <span className="text-gray-500">Sudah ditolak:</span> {selected.rejectedCount}x
                                </div>
                            </div>

                            <div>
                                {selected ? (
                                    <img
                                        src={`${import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1'}/admin/ktp/${selected.userId}/image?token=${localStorage.getItem('authToken') || ''}`}
                                        alt="Foto KTP"
                                        data-testid="ktp-modal-image"
                                        className="w-full rounded-lg border border-gray-200 dark:border-gray-700"
                                        onError={(e) => {
                                            const target = e.target as HTMLImageElement
                                            target.style.display = 'none'
                                            target.nextElementSibling?.classList.remove('hidden')
                                        }}
                                    />
                                ) : null}
                                <div className="hidden flex flex-col items-center justify-center h-48 bg-gray-100 dark:bg-white/5 rounded-lg border border-dashed border-gray-300 dark:border-white/10">
                                    <span className="material-symbols-outlined text-[32px] text-gray-400 mb-2">image_not_supported</span>
                                    <p className="text-xs text-gray-500">Gagal memuat gambar KTP</p>
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-4 space-y-3">
                            <label htmlFor="ktp-reject-reason" className="block text-sm font-medium">
                                Alasan Penolakan (min 20 karakter)
                            </label>
                            <textarea
                                id="ktp-reject-reason"
                                value={rejectReason}
                                onChange={(e) => setRejectReason(e.target.value)}
                                rows={3}
                                data-testid="ktp-reject-reason"
                                className="w-full border border-gray-300 dark:border-gray-600 rounded-md p-2 bg-white dark:bg-gray-700 text-sm"
                            />
                            {rejectError && (
                                <div role="alert" data-testid="ktp-reject-error" className="text-sm text-red-600">
                                    {rejectError}
                                </div>
                            )}
                            {actionError && (
                                <div role="alert" data-testid="ktp-action-error" className="text-sm text-red-600">
                                    {actionError}
                                </div>
                            )}
                        </div>

                        <div className="mt-4 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={closeDetail}
                                className="px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600"
                            >
                                Tutup
                            </button>
                            <button
                                type="button"
                                data-testid="ktp-reject-submit"
                                onClick={submitReject}
                                disabled={rejectMutation.isPending}
                                className="px-4 py-2 rounded-md bg-red-600 text-white disabled:opacity-50"
                            >
                                {rejectMutation.isPending ? 'Menolak...' : 'Tolak'}
                            </button>
                            <button
                                type="button"
                                data-testid="ktp-approve-submit"
                                onClick={() => selected && verifyMutation.mutate(selected.userId)}
                                disabled={verifyMutation.isPending}
                                className="px-4 py-2 rounded-md bg-green-600 text-white disabled:opacity-50"
                            >
                                {verifyMutation.isPending ? 'Menyetujui...' : 'Setujui'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default AdminKTPReviewPage
